import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import App from './App.vue';
import { apiBase, installServerSync } from './services/serverSync';

import { createRouter, createWebHashHistory } from 'vue-router';

import MenuPage from './views/Menu/MenuPage.vue';
import QuestionPage from './views/Client/QuestionPage.vue';
import CalendarPage from './views/Calendar/CalendarPage.vue';

import ClientManagePage from './views/ClientManage/ClientManagePage.vue';
import ClientPage from './views/Client/ClientPage.vue';
import ReservationPage from './views/Reservation/ReservationPage.vue';
import PlanPage from './views/Plan/PlanPage.vue';
import PricingPage from './views/Pricing/PricingPage.vue';
import MonitorPage from './views/Monitor/MonitorPage.vue';

const routes = [
  { path: '/', redirect: '/menu' },
  { path: '/login', component: () => import('./views/LoginPage.vue') },
  { path: '/master', component: () => import('./views/MasterApprovalPage.vue') },
  { path: '/m/:pathMatch(.*)*', component: () => import('./views/mobile/MobilePage.vue') },
  { path: '/menu', component: MenuPage },

  { path: '/question', component: QuestionPage },
  { path: '/calendar', component: CalendarPage },

  { path: '/patients', component: () => import('./views/PatientsPage.vue') },
  { path: '/desk', component: () => import('./views/DeskOverviewPage.vue') },
  { path: '/users-permissions', component: () => import('./views/UsersPermissionsPage.vue') },
  { path: '/reception', component: () => import('./views/ReceptionDeskPage.vue') },
  { path: '/emr', component: () => import('./views/EMRPage.vue') },
  { path: '/payments', component: () => import('./views/PaymentsPage.vue') },
  { path: '/insurance-claims', component: () => import('./views/InsuranceClaimsPage.vue') },
  { path: '/documents', component: () => import('./views/DocumentsPage.vue') },
  { path: '/questionnaire', component: () => import('./views/QuestionnairePage.vue') },
  { path: '/alerts', component: () => import('./views/AlertsRulesPage.vue') },
  { path: '/codes', component: () => import('./views/CodesMasterPage.vue') },
  { path: '/counseling', component: () => import('./views/CounselingBoardPage.vue') },
  { path: '/orders', component: () => import('./views/OrdersPage.vue') },
  { path: '/audit', component: () => import('./views/AuditLogPage.vue') },
  { path: '/analytics', component: () => import('./views/AnalyticsDashboardPage.vue') },

  { path: '/client-manage', component: ClientManagePage },
  { path: '/client', component: ClientPage },
  { path: '/reservation', component: ReservationPage },
  { path: '/plan', component: PlanPage },
  { path: '/price', component: PricingPage },
  { path: '/monitor', component: MonitorPage },

  { path: '/emr2', component: () => import('@/views/EMR_TreatmentInput.vue') },
  { path: '/plan2', component: () => import('@/views/TreatmentPlanInput.vue') },
  { path: '/desk2', component: () => import('@/views/DeskScheduling.vue') },
  { path: '/scanboard2', component: () => import('@/views/ScanAndCounseling.vue') },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((to) => {
  const isPublicPage = to.path === '/login' || to.path === '/master';
  const hasSession = Boolean(localStorage.getItem('bioa-session'));
  const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;
  const isMobilePage = to.path === '/m' || to.path.startsWith('/m/');

  if (!hasSession && !isPublicPage) {
    return '/login';
  }

  if (hasSession && isMobileViewport && !isPublicPage && !isMobilePage) {
    return to.path === '/' ? '/m/menu' : `/m${to.path}`;
  }

  if (hasSession && to.path === '/login') {
    return isMobileViewport ? '/m/menu' : '/menu';
  }

  return true;
});

async function bootstrap() {
  try {
    await installServerSync();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'BIOA 서버 연결 실패';
    document.body.innerHTML = `
      <main style="height:100vh;display:flex;align-items:center;justify-content:center;background:#f6f9fb;font-family:Arial,'Noto Sans KR',sans-serif;color:#263746;">
        <section style="width:min(560px,calc(100vw - 40px));border:1px solid #dce5ec;background:#fff;border-radius:8px;box-shadow:0 18px 40px rgba(35,49,61,.12);padding:28px;box-sizing:border-box;">
          <h1 style="margin:0 0 12px;font-size:22px;">BIOA 서버가 켜져 있지 않습니다</h1>
          <p style="margin:0 0 18px;line-height:1.55;color:#5f6e7b;">웹은 이제 Python API와 SQLite DB 값을 기준으로 동작합니다. 먼저 로컬 서버를 실행한 뒤 새로고침하세요.</p>
          <div style="display:grid;gap:8px;margin-bottom:18px;padding:12px;background:#f8fafc;border:1px solid #e3eaf0;border-radius:4px;">
            <strong>실행 파일</strong>
            <code>C:\\bioa-server\\run-bioa-all.bat</code>
            <code>C:\\bioa-server\\run-server.bat</code>
          </div>
          <p style="margin:0;color:#8a97a3;font-size:12px;">API: ${apiBase()} · ${message}</p>
        </section>
      </main>
    `;
    return;
  }

  const app = createApp(App);
  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);

  app.use(router);
  app.use(pinia);
  app.mount('#app');

  window.addEventListener('bioa-server-error', () => {
    window.alert('BIOA 서버 저장에 실패했습니다. Python 서버가 켜져 있는지 확인하세요.');
  });
}

void bootstrap();
